import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { IanTranslateService } from 'core/services/ian-core-singleton.service';
import { ChatSessionHistoryItem } from './models/chat-interface';

@Component({
  selector: 'chat-session-history-menu',
  template: `
    <div class="session-history-header" (click)="$event.stopPropagation()">
      <span class="history-title">{{ 'AI_CHAT.HISTORY.TITLE' | pTranslate }}</span>
      <button mat-icon-button (click)="onRefresh()" class="refresh-button" matTooltip="{{ 'AI_CHAT.HISTORY.REFRESH' | pTranslate }}">
        <mat-icon>refresh</mat-icon>
      </button>
    </div>

    <div class="session-history-content">
      <!-- Loading state -->
      <div *ngIf="isLoading" class="history-loading">
        <mat-spinner diameter="32"></mat-spinner>
        <span>{{ 'AI_CHAT.HISTORY.LOADING' | pTranslate }}</span>
      </div>

      <!-- Empty state -->
      <div *ngIf="!isLoading && sessions.length === 0" class="history-empty">
        <mat-icon class="empty-icon">inbox</mat-icon>
        <span>{{ 'AI_CHAT.HISTORY.EMPTY' | pTranslate }}</span>
      </div>

      <!-- Session list -->
      <div *ngIf="!isLoading && sessions.length > 0" class="session-list">
        <div
          *ngFor="let session of sessions"
          class="session-card"
          [class.active-session]="session.id === activeSessionId"
          (click)="onSessionSelect(session)"
        >
          <div class="session-date">{{ formatDate(session.date) }}</div>
          <div class="session-title">
            {{ session.name | slice : 0 : titleMaxLength }}{{ session.name?.length > titleMaxLength ? '...' : '' }}
          </div>
          <div class="session-description">{{ stripMarkdown(session.description) }}</div>
          <div class="session-meta" *ngIf="false">
            <mat-icon class="meta-icon">chat_bubble_outline</mat-icon>
            <span>{{ session.messageCount }}</span>
          </div>

          <div *ngIf="session.suggestion" class="session-suggestion-action">
            <button mat-stroked-button class="view-insight-btn" (click)="$event.stopPropagation(); onOpenSuggestion(session.suggestion)">
              <!-- <mat-icon>lightbulb</mat-icon> -->
              <span>{{ 'AI_CHAT.HISTORY.VIEW_SUGGESTION' | pTranslate }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <button mat-menu-item (click)="onNewSession()" class="new-session-button">
      <mat-icon>add_circle_outline</mat-icon>
      <span style="font-size: 14px;">{{ 'AI_CHAT.HISTORY.NEW_SESSION' | pTranslate }}</span>
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatSessionHistoryMenuComponent {
  @Input() sessions: ChatSessionHistoryItem[] = [];
  @Input() isLoading = false;
  @Input() activeSessionId: string | null = null;

  titleMaxLength = 200;

  @Output() sessionSelect = new EventEmitter<ChatSessionHistoryItem>();
  @Output() refresh = new EventEmitter<void>();
  @Output() newSession = new EventEmitter<void>();
  @Output() openSuggestion = new EventEmitter<any>();

  constructor(private translate: IanTranslateService) {}

  formatDate(date: Date | string): string {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      const key = diffMins === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.MINUTES' : 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.MINUTES_PLURAL';
      const translation = this.translate.instant(key);
      return translation ? translation.replace('{{count}}', diffMins.toString()) : `${diffMins} min`;
    } else if (diffHours < 24) {
      const key = diffHours === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.HOURS' : 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.HOURS_PLURAL';
      const translation = this.translate.instant(key);
      return translation ? translation.replace('{{count}}', diffHours.toString()) : `${diffHours} h`;
    } else if (diffDays < 7) {
      const key = diffDays === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.DAYS' : 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.DAYS_PLURAL';
      const translation = this.translate.instant(key);
      return translation ? translation.replace('{{count}}', diffDays.toString()) : `${diffDays} d`;
    } else {
      const lang = this.translate._translateService.currentLang || 'es';
      return d.toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }

  onSessionSelect(session: ChatSessionHistoryItem): void {
    this.sessionSelect.emit(session);
  }

  onOpenSuggestion(suggestion: any): void {
    this.openSuggestion.emit(suggestion);
  }

  onRefresh(): void {
    this.refresh.emit();
  }

  onNewSession(): void {
    this.newSession.emit();
  }

  stripMarkdown(text: string): string {
    if (!text) return '';
    // Basic markdown stripper for description preview
    return text
      .replace(/(\*\*|__)/g, '') // Bold
      .replace(/(\*|_)/g, '') // Italic (simplistic, removing all * and _)
      .replace(/^#+\s+/gm, '') // Headers
      .replace(/`/g, '') // Code
      .replace(/>/g, '') // Blockquotes
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links [text](url) -> text
      .replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '') // Emojis
      .trim();
  }
}
